/**
 * How Canopy WRITES a date on screen: `MM/DD/YYYY`, zero-padded (punch list
 * F193, Jeremy, 2 Nov 2026). One formatter, so every screen reads the same.
 *
 * **Calendar dates are strings and are sliced, never parsed.**
 * `new Date("2026-11-02")` is UTC midnight, which west of UTC is the evening
 * of 1 November — a due date written through it would read a day early.
 *
 * **Instants (`createdAt`, `completedAt`, ...) are written in the READER's
 * local time**, as they were before this format existed.
 */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");
/**
 * A `YYYY-MM-DD` calendar date as `MM/DD/YYYY`. A longer ISO string is
 * written by its first ten characters, the same day `.slice(0, 10)` gave.
 * Anything else is returned unchanged rather than invented.
 */
export function mdy(iso) {
    const m = ISO_DATE.exec(iso);
    if (!m)
        return iso;
    return `${m[2]}/${m[3]}/${m[1]}`;
}
/** Two calendar dates as `MM/DD/YYYY – MM/DD/YYYY`; one date when they match. */
export function mdyRange(start, end) {
    return start === end ? mdy(start) : `${mdy(start)} – ${mdy(end)}`;
}
/** A calendar date with its weekday, for a heading: `Mon 11/02/2026`. */
export function mdyWeekday(iso) {
    const m = ISO_DATE.exec(iso);
    if (!m)
        return iso;
    // Weekday by UTC arithmetic on the date itself: no timezone can move it.
    const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
    return `${WEEKDAYS[day]} ${mdy(iso)}`;
}
function toDate(instant) {
    const d = typeof instant === "string" ? new Date(instant) : instant;
    return Number.isNaN(d.getTime()) ? null : d;
}
/** The reader's local DAY of an instant, as `MM/DD/YYYY`. */
export function mdyDay(instant) {
    const d = toDate(instant);
    if (!d)
        return String(instant);
    return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}
/** An instant in the reader's local time: `11/02/2026 3:15 PM`. */
export function mdyTime(instant) {
    const d = toDate(instant);
    if (!d)
        return String(instant);
    const h = d.getHours();
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${mdyDay(d)} ${h12}:${pad(d.getMinutes())} ${h < 12 ? "AM" : "PM"}`;
}
//# sourceMappingURL=dates-display.js.map